/** @file Handle drag: the curve that puts one bezier control point of a segment at a graph position. */
import type { Curve } from '@pose2d/format/types';
import { clamp } from '@pose2d/math/clamp';
import { curveSegment } from './curveSegment';
import type { CurveTrack } from './curveTypes';

/** Which control point of which segment is dragged. */
export interface CurveHandleRef {
  segment: number;
  which: 1 | 2;
}

/** The stored form: `ease` stays ease while the segment changes value, everything else becomes bezier. */
function toCurve(track: CurveTrack, i: number, controls: [number, number, number, number]): Curve {
  const [x1, cy1, x2, cy2] = controls;
  const [v0, v1] = curveSegment.ends(track, i);
  const was = track.curves[i];
  if (typeof was === 'object' && 'ease' in was && Math.abs(v1 - v0) > 1e-12) {
    return { ease: [x1, (cy1 - v0) / (v1 - v0), x2, (cy2 - v0) / (v1 - v0)] };
  }
  return { bezier: [x1, cy1 - v0, x2, cy2 - v0] };
}

/**
 * The curve of `ref.segment` with control point `ref.which` moved to (frame, value), display units: x is the frame as
 * a fraction of the segment clamped to [0, 1] (kept as it was with `lockTime`); the value is free (overshoot allowed).
 */
export function dragCurveHandle(track: CurveTrack, ref: CurveHandleRef, frame: number, value: number, lockTime: boolean): Curve {
  const i = ref.segment;
  const f0 = track.frames[i] ?? 0;
  const f1 = track.frames[i + 1] ?? f0 + 1;
  const [x1, cy1, x2, cy2] = curveSegment.controls(track, i);
  const x = clamp((frame - f0) / (f1 - f0), 0, 1);
  if (ref.which === 1) return toCurve(track, i, [lockTime ? x1 : x, value, x2, cy2]);
  return toCurve(track, i, [x1, cy1, lockTime ? x2 : x, value]);
}
